import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { IVideo } from '@models/IVideo';
import { VideoButton } from '@molecules/VideoButton/VideoButton';
import classNames from 'classnames';
import { useRef, useState } from 'react';

interface IHeroMedia {
  className: string;
  image?: IImage;
  mobileImage?: IImage;
  video?: IVideo;
  hasPlayButton?: boolean;
  darkFont?: boolean;
  playButtonLabel: string;
  videoConfig?: string;
  isSplit: boolean;
  autoPlay?: boolean;
  muted?: boolean;
}

const HeroImageComponent = ({
  src,
  className,
  width,
  height,
  isSplit,
}: {
  src: IImage;
  className: string;
  width?: number;
  height?: number;
  isSplit: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <Image
      image={src}
      classes={classNames(
        'tw-min-w-full tw-min-h-full tw-h-full',
        loaded ? 'tw-opacity-100' : 'tw-opacity-0',
        className
      )}
      imageClasses="tw-min-w-full tw-min-h-full tw-h-full tw-object-cover"
      width={width}
      height={height}
      lazyLoad={false}
      onLoad={() => setLoaded(true)}
      sizes={isSplit ? '(max-width: 1408px) 100vw, 50vw' : '100vw'}
    />
  );
};

export const HeroMedia = ({
  className,
  image,
  video,
  hasPlayButton,
  darkFont = false,
  playButtonLabel,
  videoConfig = '',
  mobileImage,
  isSplit,
  autoPlay = true,
  muted = true,
}: IHeroMedia) => {
  const [videoError, setVideoError] = useState(false);
  const [playVideo, setPlayVideo] = useState(autoPlay);
  const videoRef = useRef<HTMLVideoElement>(null);

  const heroImage = (
    <>
      {mobileImage ? (
        <HeroImageComponent
          src={mobileImage}
          className="md:tw-hidden"
          width={900}
          height={1000}
          isSplit={isSplit}
        />
      ) : null}
      {image ? (
        <HeroImageComponent
          src={image}
          height={800}
          className={mobileImage ? 'max-md:tw-hidden' : ''}
          isSplit={isSplit}
        />
      ) : null}
    </>
  );

  return (
    <div className={classNames(className, '')}>
      {image && (!video || videoError || hasPlayButton) ? heroImage : null}
      {hasPlayButton && video ? (
        <div className="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2">
          <VideoButton
            darkFont={darkFont}
            playButtonLabel={playButtonLabel}
            videoSource={video.videoSource}
            videoConfig={videoConfig}
          />
        </div>
      ) : null}
      {video && !hasPlayButton ? (
        <div className="tw-relative tw-h-full tw-w-full">
          <video
            ref={videoRef}
            preload="metadata"
            loop
            controls={playVideo && !autoPlay}
            playsInline
            autoPlay={autoPlay}
            muted={muted}
            className="tw-h-full tw-w-full tw-object-cover tw-object-center"
            onError={() => setVideoError(true)}
          >
            <source src={`${video.videoSource}#t=0.1`} type="video/mp4" />
            {/* Fallback image */}
            {image ? heroImage : null}
          </video>
          {!playVideo && !autoPlay ? (
            <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-z-50 -tw-translate-x-1/2 -tw-translate-y-1/2">
              <VideoButton
                darkFont={darkFont}
                playButtonLabel={playButtonLabel}
                onClick={() => {
                  videoRef.current?.play();
                  setPlayVideo(!playVideo);
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
